/** Convert Google Drive / Docs / Forms links to embed-friendly URLs for MPL-10 activity viewer. */

export type ActivityViewerType = 'iframe' | 'image' | 'video' | 'download';

export function inferViewerTypeFromExtension(extension: string | undefined): ActivityViewerType {
  const ext = String(extension ?? '').toLowerCase();
  if (ext === 'jpg' || ext === 'jpeg' || ext === 'png' || ext === 'webp' || ext === 'gif') return 'image';
  if (ext === 'mp4' || ext === 'webm' || ext === 'mov') return 'video';
  if (ext === 'pdf') return 'iframe';
  return 'download';
}

export function inferViewerTypeFromMime(mimeType: string | undefined): ActivityViewerType {
  if (!mimeType) return 'iframe';
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType === 'application/pdf') return 'iframe';
  return 'download';
}

export function toActivityEmbedUrl(url: string): string {
  const trimmed = url.trim();

  const docMatch = trimmed.match(/docs\.google\.com\/document\/d\/([^/]+)/i);
  if (docMatch) {
    return `https://docs.google.com/document/d/${docMatch[1]}/preview`;
  }

  const formPublishedMatch = trimmed.match(/docs\.google\.com\/forms\/d\/e\/([^/]+)/i);
  if (formPublishedMatch) {
    const base = trimmed.split('?')[0].replace(/\/viewform.*$/i, '');
    return `${base}/viewform?embedded=true`;
  }

  const formEditMatch = trimmed.match(/docs\.google\.com\/forms\/d\/(?!e\/)([^/]+)/i);
  if (formEditMatch) {
    return `https://docs.google.com/forms/d/${formEditMatch[1]}/viewform?embedded=true`;
  }

  const youtubeMatch = trimmed.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([A-Za-z0-9_-]+)/i);
  if (youtubeMatch) {
    return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
  }

  const driveMatch = trimmed.match(/drive\.google\.com\/file\/d\/([^/]+)/i);
  if (driveMatch) {
    return `https://drive.google.com/file/d/${driveMatch[1]}/preview`;
  }

  if (/forms\.gle\//i.test(trimmed)) {
    return trimmed.includes('embedded=true') ? trimmed : `${trimmed}${trimmed.includes('?') ? '&' : '?'}embedded=true`;
  }

  return trimmed;
}

export function isEmbeddableHttpUrl(url: string): boolean {
  return /^https?:\/\//i.test(url.trim());
}
